import React, { useState } from "react";
import axios from '@common/axios';

const GEOGEBRA_RESOURCE_URL = "https://www.geogebra.org/math";

// GeoGebra's known topic taxonomy extracted from their Math Topic Map
// Source: geogebra.org/math and help.geogebra.org topic map documentation
const GEOGEBRA_TAXONOMY = {
  "Arithmetic": [
    "Numbers", "Place Value", "Addition", "Subtraction", "Multiplication", "Division",
    "Fractions", "Decimals", "Percentages", "Ratios", "Proportions", "Factors and Multiples",
    "Prime Numbers", "Integers", "Rounding", "Estimation", "Order of Operations",
    "Powers and Roots", "Scientific Notation"
  ],
  "Algebra": [
    "Expressions", "Equations", "Inequalities", "Linear Equations", "Linear Inequalities",
    "Systems of Equations", "Quadratic Equations", "Polynomials", "Factoring",
    "Rational Expressions", "Exponents", "Radicals", "Absolute Value",
    "Sequences and Series", "Arithmetic Sequences", "Geometric Sequences",
    "Logarithms", "Exponential Functions", "Matrices", "Vectors"
  ],
  "Geometry": [
    "Points, Lines and Planes", "Angles", "Triangles", "Quadrilaterals", "Polygons",
    "Circles", "Perimeter", "Area", "Surface Area", "Volume",
    "Congruence", "Similarity", "Transformations", "Reflections", "Rotations",
    "Translations", "Dilations", "Pythagorean Theorem", "Coordinate Geometry",
    "3D Geometry", "Constructions", "Proofs"
  ],
  "Functions": [
    "Introduction to Functions", "Domain and Range", "Function Notation",
    "Linear Functions", "Quadratic Functions", "Polynomial Functions",
    "Rational Functions", "Exponential Functions", "Logarithmic Functions",
    "Piecewise Functions", "Inverse Functions", "Composition of Functions",
    "Transformations of Functions", "Graphing Functions", "Slope", "Intercepts"
  ],
  "Trigonometry": [
    "Right Triangle Trigonometry", "Sine", "Cosine", "Tangent",
    "Trigonometric Ratios", "Unit Circle", "Trigonometric Functions",
    "Trigonometric Identities", "Trigonometric Equations",
    "Law of Sines", "Law of Cosines", "Radians and Degrees",
    "Graphs of Trigonometric Functions", "Inverse Trigonometric Functions",
    "Periodic Functions", "Amplitude and Period"
  ],
  "Calculus": [
    "Limits", "Continuity", "Derivatives", "Differentiation Rules",
    "Applications of Derivatives", "Related Rates", "Optimization",
    "Integrals", "Definite Integrals", "Indefinite Integrals",
    "Fundamental Theorem of Calculus", "Applications of Integration",
    "Area Between Curves", "Volumes of Revolution", "Sequences and Series",
    "Differential Equations"
  ],
  "Statistics": [
    "Data Collection", "Data Display", "Bar Charts", "Pie Charts", "Histograms",
    "Box Plots", "Scatter Plots", "Mean", "Median", "Mode", "Range",
    "Measures of Spread", "Standard Deviation", "Variance",
    "Normal Distribution", "Sampling", "Correlation", "Regression",
    "Line of Best Fit", "Statistical Inference"
  ],
  "Probability": [
    "Basic Probability", "Sample Space", "Events", "Complementary Events",
    "Independent Events", "Conditional Probability", "Mutually Exclusive Events",
    "Combinations", "Permutations", "Counting Principles",
    "Binomial Distribution", "Expected Value", "Tree Diagrams",
    "Venn Diagrams", "Simulations"
  ],
  "Numbers": [
    "Natural Numbers", "Whole Numbers", "Rational Numbers", "Irrational Numbers",
    "Real Numbers", "Complex Numbers", "Number Lines", "Comparing Numbers",
    "Number Systems", "Surds"
  ],
  "Measurement": [
    "Length", "Mass and Weight", "Capacity and Volume", "Time", "Temperature",
    "Area Measurement", "Perimeter Measurement", "Units of Measurement",
    "Unit Conversion", "Scale and Maps"
  ]
};

const allTopics = Object.entries(GEOGEBRA_TAXONOMY).flatMap(([category, subtopics]) =>
  subtopics.map(sub => ({ category, topic: sub, url: `https://www.geogebra.org/search/${encodeURIComponent(sub)}` }))
);

export default function GeoGebraTaxonomy({ currentQuestion, conceptId }) {
  const [search, setSearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState(
    Object.fromEntries(Object.keys(GEOGEBRA_TAXONOMY).map(k => [k, true]))
  );
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState("browse"); // browse | json | prompt | manual
  const [manualAppletId, setManualAppletId] = useState("");
  const [savingMaterial, setSavingMaterial] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const toggleCategory = (cat) =>
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const filtered = search.trim()
    ? allTopics.filter(t =>
        t.topic.toLowerCase().includes(search.toLowerCase()) ||
        t.category.toLowerCase().includes(search.toLowerCase())
      )
    : null;

  const totalTopics = allTopics.length;

  const jsonOutput = JSON.stringify(GEOGEBRA_TAXONOMY, null, 2);

  const promptOutput = `You are a mathematics question classifier. Your task is to classify a school-level maths question into the most appropriate topic from GeoGebra's official topic taxonomy.

RULES:
1. You MUST choose exactly one topic from the list below
2. Choose the most specific topic that applies
3. If a question spans multiple topics, choose the PRIMARY concept being tested
4. If no topic fits well, return "UNCLASSIFIED" and briefly explain why
5. Return your answer as JSON: {"category": "...", "topic": "...", "confidence": "high|medium|low", "reasoning": "..."}

GEOGEBRA TOPIC TAXONOMY:
${Object.entries(GEOGEBRA_TAXONOMY).map(([cat, topics]) =>
  `\n${cat}:\n${topics.map(t => `  - ${t}`).join('\n')}`
).join('')}

Now classify the following question:
[PASTE QUESTION HERE]`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Save GeoGebra material to current question
  const saveMaterialToQuestion = async (appletId) => {
    if (!currentQuestion?.questionId || !appletId || !appletId.trim()) {
      setSaveError('Cannot save: No question or applet ID provided');
      return;
    }

    setSavingMaterial(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await axios.post('/web/ui/question/geogebra/link', null, {
        params: {
          questionId: currentQuestion.questionId,
          conceptId: conceptId,
          geogebraAppletId: appletId.trim()
        }
      });

      setSaveSuccess(true);
      setManualAppletId("");
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save GeoGebra material:', err);
      setSaveError(`Failed to save material: ${err.response?.data?.message || err.message}`);
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setSavingMaterial(false);
    }
  };

  const categoryColors = {
    "Arithmetic": "#e74c3c",
    "Algebra": "#e67e22",
    "Geometry": "#27ae60",
    "Functions": "#2980b9",
    "Trigonometry": "#8e44ad",
    "Calculus": "#c0392b",
    "Statistics": "#16a085",
    "Probability": "#d35400",
    "Numbers": "#2c3e50",
    "Measurement": "#7f8c8d",
  };

  return (
    <div style={{
      fontFamily: "'Georgia', serif",
      background: "#0f1117",
      minHeight: "100vh",
      color: "#e8e0d0",
      padding: "0",
    }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #1a1f2e 0%, #0f1117 100%)",
        borderBottom: "1px solid #2a2f3e",
        padding: "32px 40px 24px",
      }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
            <span style={{ fontSize: 11, letterSpacing: 4, color: "#4a9eff", textTransform: "uppercase", fontFamily: "monospace" }}>GeoGebra</span>
            <span style={{ color: "#2a3a55" }}>◆</span>
            <span style={{ fontSize: 11, letterSpacing: 4, color: "#666", textTransform: "uppercase", fontFamily: "monospace" }}>Topic Taxonomy</span>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: "normal", margin: "0 0 8px", color: "#f0ebe0", letterSpacing: -0.5 }}>
            Math Topic Map
          </h1>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <p style={{ margin: 0, color: "#888", fontSize: 14, lineHeight: 1.6 }}>
              {Object.keys(GEOGEBRA_TAXONOMY).length} categories · {totalTopics} topics · Ready to use as a Claude classifier prompt
            </p>
            {currentQuestion && (
              <div style={{
                background: "#1a2530",
                border: "1px solid #2a3a55",
                borderRadius: 4,
                padding: "6px 12px",
                fontSize: 12,
                fontFamily: "monospace",
                color: "#4a9eff"
              }}>
                Linking to Question {currentQuestion.questionId}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nav */}
      <div style={{ borderBottom: "1px solid #1e2330", background: "#13161f" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", gap: 0 }}>
          {[["browse", "Browse Topics"], ["manual", "Link Applet"], ["json", "JSON Export"], ["prompt", "Claude Prompt"]].map(([id, label]) => (
            <button key={id} onClick={() => setView(id)} style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "14px 24px", fontSize: 13,
              color: view === id ? "#4a9eff" : "#666",
              borderBottom: view === id ? "2px solid #4a9eff" : "2px solid transparent",
              fontFamily: "monospace", letterSpacing: 0.5,
              transition: "color 0.2s",
            }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 40px" }}>

        {/* BROWSE VIEW */}
        {view === "browse" && (
          <>
            {/* Search */}
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search topics..."
              style={{
                width: "100%", boxSizing: "border-box",
                background: "#1a1f2e", border: "1px solid #2a3040",
                borderRadius: 6, padding: "12px 16px",
                color: "#e8e0d0", fontSize: 14, fontFamily: "monospace",
                outline: "none", marginBottom: 28,
              }}
            />

            {filtered ? (
              // Search results
              <div>
                <p style={{ color: "#666", fontSize: 12, fontFamily: "monospace", marginBottom: 16 }}>
                  {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "{search}"
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {filtered.map(({ category, topic, url }) => (
                    <a key={`${category}-${topic}`} href={url} target="_blank" rel="noreferrer"
                      style={{
                        background: "#1a1f2e", border: `1px solid ${categoryColors[category]}44`,
                        borderLeft: `3px solid ${categoryColors[category]}`,
                        borderRadius: 4, padding: "8px 14px",
                        color: "#e8e0d0", textDecoration: "none", fontSize: 13,
                        display: "flex", flexDirection: "column", gap: 2,
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#22293a"}
                      onMouseLeave={e => e.currentTarget.style.background = "#1a1f2e"}
                    >
                      <span>{topic}</span>
                      <span style={{ fontSize: 10, color: categoryColors[category], fontFamily: "monospace" }}>{category}</span>
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              // Full taxonomy
              Object.entries(GEOGEBRA_TAXONOMY).map(([category, topics]) => (
                <div key={category} style={{ marginBottom: 24 }}>
                  <button onClick={() => toggleCategory(category)} style={{
                    background: "none", border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%", textAlign: "left", marginBottom: 12, padding: 0,
                  }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: 2,
                      background: categoryColors[category],
                      flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 16, color: "#f0ebe0", letterSpacing: 0.3 }}>{category}</span>
                    <span style={{ fontSize: 11, color: "#555", fontFamily: "monospace", marginLeft: 4 }}>
                      {topics.length} topics
                    </span>
                    <span style={{ marginLeft: "auto", color: "#444", fontSize: 12 }}>
                      {expandedCategories[category] ? "▼" : "▶"}
                    </span>
                  </button>

                  {expandedCategories[category] && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingLeft: 20 }}>
                      {topics.map(topic => (
                        <a
                          key={topic}
                          href={`https://www.geogebra.org/search/${encodeURIComponent(topic)}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            background: "#1a1f2e",
                            border: `1px solid #2a3040`,
                            borderBottom: `2px solid ${categoryColors[category]}66`,
                            borderRadius: 4, padding: "7px 13px",
                            color: "#c8c0b0", textDecoration: "none", fontSize: 13,
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = "#22293a";
                            e.currentTarget.style.color = "#f0ebe0";
                            e.currentTarget.style.borderBottomColor = categoryColors[category];
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = "#1a1f2e";
                            e.currentTarget.style.color = "#c8c0b0";
                            e.currentTarget.style.borderBottomColor = `${categoryColors[category]}66`;
                          }}
                        >
                          {topic}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
            <p style={{ marginTop: 32, fontSize: 12, color: "#444", fontFamily: "monospace", borderTop: "1px solid #1e2330", paddingTop: 16 }}>
              Each topic links directly to GeoGebra search results → click any topic to explore available activities
            </p>
          </>
        )}

        {/* JSON VIEW */}
        {view === "json" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <p style={{ margin: 0, color: "#666", fontSize: 13, fontFamily: "monospace" }}>
                Paste this into your system as the canonical topic list
              </p>
              <button onClick={() => copyToClipboard(jsonOutput)} style={{
                background: copied ? "#27ae60" : "#1a1f2e",
                border: "1px solid #2a3040", borderRadius: 4,
                color: copied ? "#fff" : "#4a9eff",
                padding: "8px 16px", cursor: "pointer", fontSize: 12,
                fontFamily: "monospace", transition: "all 0.2s",
              }}>
                {copied ? "✓ Copied" : "Copy JSON"}
              </button>
            </div>
            <pre style={{
              background: "#1a1f2e", border: "1px solid #2a3040",
              borderRadius: 6, padding: 20, overflow: "auto",
              fontSize: 12, lineHeight: 1.6, color: "#a8c0d0",
              fontFamily: "monospace", maxHeight: 520,
            }}>
              {jsonOutput}
            </pre>
          </div>
        )}

        {/* MANUAL LINK VIEW */}
        {view === "manual" && (
          <div>
            {/* Current Question Context */}
            {currentQuestion && (
              <div style={{
                background: "#1a1f2e",
                border: "1px solid #2a3a55",
                borderLeft: "3px solid #4a9eff",
                borderRadius: 4,
                padding: 12,
                marginBottom: 16,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontFamily: "monospace", color: "#4a9eff", fontWeight: "bold" }}>
                    LINKING MODE
                  </span>
                  <span style={{ fontSize: 11, color: "#666" }}>•</span>
                  <span style={{ fontSize: 12, color: "#888" }}>
                    Question ID: {currentQuestion.questionId}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: "#999", lineHeight: 1.5 }}>
                  Enter a GeoGebra applet ID to link it with this question
                </p>
                {currentQuestion.geogebraAppletId && (
                  <p style={{ margin: "6px 0 0", fontSize: 11, color: "#666", fontFamily: "monospace" }}>
                    Currently linked: {currentQuestion.geogebraAppletId}
                  </p>
                )}
              </div>
            )}

            {/* Save Status Messages */}
            {saveError && (
              <div style={{
                background: "#2a1a1a",
                border: "1px solid #4a2020",
                borderLeft: "3px solid #e74c3c",
                borderRadius: 4,
                padding: 12,
                marginBottom: 16,
              }}>
                <p style={{ margin: 0, fontSize: 13, color: "#e8a0a0" }}>
                  {saveError}
                </p>
              </div>
            )}

            {saveSuccess && (
              <div style={{
                background: "#1a2a1a",
                border: "1px solid #204a20",
                borderLeft: "3px solid #27ae60",
                borderRadius: 4,
                padding: 12,
                marginBottom: 16,
              }}>
                <p style={{ margin: 0, fontSize: 13, color: "#a0e8a0" }}>
                  ✓ Material saved successfully!
                </p>
              </div>
            )}

            {/* Instructions */}
            <div style={{
              background: "#1a1f2e", border: "1px solid #2a3040",
              borderLeft: "3px solid #4a9eff", borderRadius: 4, padding: 16,
              marginBottom: 20,
            }}>
              <p style={{ margin: "0 0 12px", fontSize: 14, color: "#f0ebe0" }}>
                <strong>Link GeoGebra Applet</strong>
              </p>
              <p style={{ margin: "0 0 8px", fontSize: 13, color: "#888", lineHeight: 1.7 }}>
                Browse GeoGebra materials using the topics above, then copy the applet ID from the URL and paste it here.
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#666", fontFamily: "monospace", lineHeight: 1.6 }}>
                Example: https://www.geogebra.org/m/<strong style={{ color: "#4a9eff" }}>abc123xyz</strong> → Enter "abc123xyz"
              </p>
            </div>

            {/* Manual Input Form */}
            <div style={{
              background: "#1a1f2e",
              border: "1px solid #2a3040",
              borderRadius: 6,
              padding: 20,
            }}>
              <label style={{
                display: "block",
                fontSize: 13,
                color: "#e8e0d0",
                marginBottom: 8,
                fontFamily: "monospace",
              }}>
                GeoGebra Applet ID
              </label>
              <div style={{ display: "flex", gap: 12 }}>
                <input
                  value={manualAppletId}
                  onChange={e => setManualAppletId(e.target.value)}
                  placeholder="e.g., abc123xyz"
                  style={{
                    flex: 1,
                    background: "#0f1117",
                    border: "1px solid #2a3040",
                    borderRadius: 4,
                    padding: "12px 16px",
                    color: "#e8e0d0",
                    fontSize: 14,
                    fontFamily: "monospace",
                    outline: "none",
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && manualAppletId.trim() && currentQuestion && !savingMaterial) {
                      saveMaterialToQuestion(manualAppletId);
                    }
                  }}
                />
                <button
                  onClick={() => saveMaterialToQuestion(manualAppletId)}
                  disabled={!manualAppletId.trim() || !currentQuestion || savingMaterial}
                  style={{
                    background: !manualAppletId.trim() || !currentQuestion || savingMaterial ? "#2a3040" : "#27ae60",
                    border: "none",
                    borderRadius: 4,
                    color: !manualAppletId.trim() || !currentQuestion || savingMaterial ? "#666" : "#fff",
                    padding: "12px 24px",
                    fontSize: 13,
                    fontFamily: "monospace",
                    cursor: !manualAppletId.trim() || !currentQuestion || savingMaterial ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={e => {
                    if (manualAppletId.trim() && currentQuestion && !savingMaterial) {
                      e.currentTarget.style.background = "#229954";
                    }
                  }}
                  onMouseLeave={e => {
                    if (manualAppletId.trim() && currentQuestion && !savingMaterial) {
                      e.currentTarget.style.background = "#27ae60";
                    }
                  }}
                >
                  {savingMaterial ? "Saving..." : "Save"}
                </button>
              </div>
              {!currentQuestion && (
                <p style={{ margin: "8px 0 0", fontSize: 12, color: "#888", fontFamily: "monospace" }}>
                  ⚠ No question selected. Open this from a question to link an applet.
                </p>
              )}
            </div>

            {/* Quick Links Section */}
            <div style={{ marginTop: 32 }}>
              <h3 style={{ fontSize: 14, color: "#f0ebe0", marginBottom: 12 }}>
                Quick Links
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <a
                  href="https://www.geogebra.org/materials"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    background: "#1a1f2e",
                    border: "1px solid #2a3040",
                    borderRadius: 4,
                    padding: "12px 16px",
                    color: "#4a9eff",
                    textDecoration: "none",
                    fontSize: 13,
                    fontFamily: "monospace",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = "#22293a";
                    e.currentTarget.style.borderColor = "#4a9eff";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "#1a1f2e";
                    e.currentTarget.style.borderColor = "#2a3040";
                  }}
                >
                  <span>🔍</span>
                  <span>Browse All GeoGebra Materials</span>
                </a>
                <a
                  href="https://www.geogebra.org/calculator"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    background: "#1a1f2e",
                    border: "1px solid #2a3040",
                    borderRadius: 4,
                    padding: "12px 16px",
                    color: "#4a9eff",
                    textDecoration: "none",
                    fontSize: 13,
                    fontFamily: "monospace",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = "#22293a";
                    e.currentTarget.style.borderColor = "#4a9eff";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "#1a1f2e";
                    e.currentTarget.style.borderColor = "#2a3040";
                  }}
                >
                  <span>🧮</span>
                  <span>Open GeoGebra Calculator</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* PROMPT VIEW */}
        {view === "prompt" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <p style={{ margin: 0, color: "#666", fontSize: 13, fontFamily: "monospace" }}>
                Ready-to-use Claude system prompt — constrains classification to GeoGebra topics only
              </p>
              <button onClick={() => copyToClipboard(promptOutput)} style={{
                background: copied ? "#27ae60" : "#1a1f2e",
                border: "1px solid #2a3040", borderRadius: 4,
                color: copied ? "#fff" : "#4a9eff",
                padding: "8px 16px", cursor: "pointer", fontSize: 12,
                fontFamily: "monospace", transition: "all 0.2s",
              }}>
                {copied ? "✓ Copied" : "Copy Prompt"}
              </button>
            </div>
            <pre style={{
              background: "#1a1f2e", border: "1px solid #2a3040",
              borderRadius: 6, padding: 20, overflow: "auto",
              fontSize: 12, lineHeight: 1.8, color: "#a8c0d0",
              fontFamily: "monospace", maxHeight: 520, whiteSpace: "pre-wrap",
            }}>
              {promptOutput}
            </pre>
            <div style={{
              marginTop: 20, background: "#1a1f2e", border: "1px solid #2a3040",
              borderLeft: "3px solid #4a9eff", borderRadius: 4, padding: 16,
            }}>
              <p style={{ margin: 0, fontSize: 13, color: "#888", lineHeight: 1.7 }}>
                <span style={{ color: "#4a9eff", fontFamily: "monospace" }}>Usage: </span>
                Use this as the <code style={{ background: "#0f1117", padding: "1px 6px", borderRadius: 3, color: "#e8e0d0" }}>system</code> prompt when calling Claude Sonnet via the API. 
                Replace <code style={{ background: "#0f1117", padding: "1px 6px", borderRadius: 3, color: "#e8e0d0" }}>[PASTE QUESTION HERE]</code> with your XHTML question content in the <code style={{ background: "#0f1117", padding: "1px 6px", borderRadius: 3, color: "#e8e0d0" }}>user</code> message.
                Claude will return a JSON object with category, topic, confidence and reasoning.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
